import { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { Sparkles, Loader2 } from "lucide-react";

export default function Login() {
  const { user, login, loading } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  if (loading) return null;
  if (user) return <Navigate to="/" replace />;

  async function onSubmit(e) {
    e.preventDefault();
    setErr(""); setBusy(true);
    try {
      await login(email, password);
      nav("/");
    } catch (e) {
      setErr(e.response?.data?.detail || "Login failed");
    } finally { setBusy(false); }
  }

  const QUICK_ACCOUNTS = [
    { label: "Admin", email: "admin@saarv.com" },
    { label: "CRO", email: "leadership@saarv.com" },
    { label: "Partner", email: "rajesh@envera.in" },
  ];

  return (
    <div className="min-h-screen w-full flex bg-[var(--bg)] text-[var(--text)]" data-testid="login-page">
      {/* Left — form */}
      <div className="flex-1 flex items-center justify-center px-8">
        <div className="w-full max-w-sm">
          <div className="flex items-center gap-3 mb-10">
            <img src="/saarv-logo.png" alt="Saarv" className="h-11 w-auto" />
            <div className="pl-3 border-l border-black/10">
              <div className="font-display text-[11px] tracking-[0.2em] text-[var(--saarv-blue)] leading-none">SaarvOS</div>
              <div className="text-[10px] uppercase tracking-widest text-[var(--text-dim)] mt-1.5">Rise with knowledge</div>
            </div>
          </div>

          <h1 className="font-display text-3xl mb-2 text-[var(--saarv-blue)]">The revenue engine.</h1>
          <p className="text-[var(--text-muted)] text-sm mb-8">Sign in to your AI-native operating system.</p>

          <form onSubmit={onSubmit} className="space-y-3">
            <div>
              <label className="text-[11px] uppercase tracking-widest text-[var(--text-dim)]">Email</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                data-testid="login-email"
                className="w-full mt-1 h-10 px-3 rounded-md bg-white border border-black/15 focus:border-[var(--saarv-blue)] outline-none text-sm text-[var(--text)]"
              />
            </div>
            <div>
              <label className="text-[11px] uppercase tracking-widest text-[var(--text-dim)]">Password</label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                data-testid="login-password"
                className="w-full mt-1 h-10 px-3 rounded-md bg-white border border-black/15 focus:border-[var(--saarv-blue)] outline-none text-sm text-[var(--text)]"
              />
            </div>
            {err && <div className="text-[12px] text-[var(--danger)]" data-testid="login-error">{err}</div>}
            <button
              type="submit"
              disabled={busy}
              data-testid="login-submit"
              className="w-full h-10 rounded-md bg-[var(--saarv-blue)] hover:bg-[var(--saarv-blue-strong)] transition-colors text-white font-medium text-sm flex items-center justify-center gap-2 disabled:opacity-50"
            >
              {busy ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />}
              Sign In
            </button>
          </form>

          <div className="mt-6 pt-6 border-t border-black/10">
            {/* Fills the email only — the password is never shipped in the bundle. */}
            <div className="text-[10px] uppercase tracking-widest text-[var(--text-dim)] mb-2">Quick access · fills email</div>
            <div className="flex gap-2">
              {QUICK_ACCOUNTS.map(a => (
                <button
                  key={a.email}
                  onClick={() => { setEmail(a.email); setPassword(""); }}
                  data-testid={`quick-login-${a.label.toLowerCase()}`}
                  className="flex-1 h-8 text-[11px] rounded-md bg-white border border-black/15 hover:border-[var(--saarv-blue)]/50 hover:bg-[var(--saarv-blue-soft)] transition-colors text-[var(--text)]"
                >
                  {a.label}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Right — industrial hero */}
      <div className="hidden lg:block flex-1 relative overflow-hidden border-l border-white/5">
        <div
          className="absolute inset-0"
          style={{
            backgroundImage: "url(https://images.pexels.com/photos/18568174/pexels-photo-18568174.jpeg)",
            backgroundSize: "cover", backgroundPosition: "center",
            filter: "grayscale(0.6) brightness(0.4)",
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-tr from-[var(--bg)] via-transparent to-transparent" />
        <div className="absolute bottom-10 left-10 right-10">
          <div className="text-[10px] uppercase tracking-widest text-[var(--copper)] mb-3">Industrial B2B · AI-Native</div>
          <div className="font-display text-4xl leading-tight tracking-tight max-w-lg">
            One digital thread from market signal to renewal.
          </div>
          <div className="text-[13px] text-[var(--text-muted)] mt-4 max-w-md leading-relaxed">
            Universe intelligence · 5-gate opportunity funnel · buying committee tracking · Ask Niq execution · partner maturity model.
          </div>
        </div>
      </div>
    </div>
  );
}
